import { axiosGet } from '@/api/handlers';
import { useQuery } from '@/api/hooks';
import { OrganizationsModel } from '@/features/tenant-manager/models';
import { setUser } from '@/lib/datadog';
import useUmami from '@/lib/umami';
import {
  genRecursiveOptions,
  getRecursiveArray,
} from '@/utils/array-formatter';
import type { z } from 'zod';
import { MyProfileModel } from '../_models/me';
import { authenticationQueryKeyMap } from '../_utils/query-keys';

export const useMe = <T = z.infer<typeof MyProfileModel>>(
  select?: (data: z.infer<typeof MyProfileModel>) => T,
) => {
  const umami = useUmami();
  return useQuery({
    queryKey: authenticationQueryKeyMap.me(),
    queryFn: async () => {
      const me = await axiosGet('/api/me/profile/');
      const res = MyProfileModel.parse(me);
      setUser(res.id, res);
      umami.identify(res);
      return res;
    },
    select,
    cacheTime: 5 * 60 * 1000,
    staleTime: 5 * 60 * 500,
  });
};

const OrganizationsModelElement = OrganizationsModel.element;

const useMyOrganizations = <
  T = Array<z.infer<typeof OrganizationsModelElement>>,
>(
  select?: (data: z.infer<typeof OrganizationsModel>) => T,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: ['myOrganizations'],
    queryFn: async () => {
      const organizations = await axiosGet('/api/me/organizations/');
      return OrganizationsModel.parse(organizations);
    },
    select,
    enabled,
  });
};

export const useMyOrganizationOptions = () => {
  const options = useMyOrganizations((data) =>
    genRecursiveOptions(
      getRecursiveArray(
        data.filter((d) => !d.upperId),
        data,
      ),
      'id',
      'name',
    ),
  );
  return options;
};
